import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TableList from '../../../../core/table/TableList';
import TableTop from '../../../../core/table/TableTop';
import TableHead from '../../../../core/table/TableHead';
import TableBody from '../../../../core/table/TableBody';
import TablePaging from '../../../../core/table/TablePaging';
import Dialog from '../../../../core/components/Dialog';
import Button from '../../../../core/components/Button';
import TableSkeleton from '../../../../core/components/TableSkeleton';
import { useSetImmerAtom } from 'jotai-immer';
import { useMonitorStatusTableInit } from './useMonitorStatusTableInit';
import { useMonitorStatusTableState } from './useMonitorStatusTableState';
import { useMonitorStatusData } from './useMonitorStatusData';
import { useDataMonitorStore } from '../pagehooks/useDataMonitorStore';
import { monitorStatusDataAtom } from './monitorstatus_atoms';
import MonitorStatusSearchForm from '../components/MonitorStatusSearchForm';
import MonitorConfigEditFlyOut from '../components/MonitorConfigEditFlyOut';
import datamonitorRepo, { MonitorItem, DatafeedQuery } from '../../data/datamonitorRepo';

// full Verity monitor-type list used for the in-table MonitorType filter options
const VERITY_MONITOR_TYPES: string[] = [
    'AFFILIATION.ACKNOWLEDGEMENT.RECEIVED',
    'AFFILIATION.CATALOG.RECEIVED',
    'AFFILIATION.INVOICES.RECEIVED',
    'AFFILIATION.SPLITORDER.RECEIVED',
    'AFFILIATION.SPLITTRANSACTIONS.RECEIVED',
    'COVEREDENTITY.CDC.RESTOCK',
    'COVEREDENTITY.CDC.RESTOCK_EXCEPTION',
    'COVEREDENTITY.ENCOUNTERS.RECEIVED',
    'COVEREDENTITY.EPRESCRIBE.RECEIVED',
    'COVEREDENTITY.NPI.RECEIVED',
    'COVEREDENTITY.REFNPI.RECEIVED',
    'ORDER_IN',
    'ORDER_OUT',
    'PHARMACY.DISPENSE.RECEIVED',
    'SWITCH.DISPENSE.RECEIVED',
    'VENDOR.TPA.INVOICEFILE.RECEIVED',
    'VENDOR.TPA.MATCHES.SENT',
    'VENDOR.TPA.QBEXPORT',
    'VENDOR.TPA.RECONFILE',
];

type MonitorStatusVerityTableProps = {
    isVeritySA: boolean;
};

type BulkPrompt = {
    subscribed: boolean;
    query: DatafeedQuery;
    count: number;
};

const MonitorStatusVerityTable = ({ isVeritySA }: MonitorStatusVerityTableProps): JSX.Element => {
    const setData = useSetImmerAtom(monitorStatusDataAtom);
    const { deepLinkMonitorType } = useDataMonitorStore();
    const [subscribeError, setSubscribeError] = useState(false);
    const [infoMsg, setInfoMsg] = useState('');
    const [bulkPrompt, setBulkPrompt] = useState<BulkPrompt | null>(null);
    const lastQueryRef = useRef<DatafeedQuery | null>(null);

    const handleSubscribeToggle = useCallback(
        async (item: any, colKey: string, newVal: boolean): Promise<boolean> => {
            const oldVal = item[colKey];
            const newStr = newVal ? 'ON' : 'OFF';
            setData(draft => {
                const target = draft.dataItems.find((d: any) => d.Id === item.Id);
                if (target) (target as any)[colKey] = newStr;
            });
            const email = colKey === 'SubscribedEmail' ? newVal : item.SubscribedEmail === 'ON';
            const inApp = colKey === 'SubscribedInApp' ? newVal : item.SubscribedInApp === 'ON';
            try {
                await datamonitorRepo.subscribe(item.Id, email, inApp);
                return true;
            } catch {
                setData(draft => {
                    const target = draft.dataItems.find((d: any) => d.Id === item.Id);
                    if (target) (target as any)[colKey] = oldVal;
                });
                setSubscribeError(true);
                return false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const { loadVerityMonitors } = useMonitorStatusTableInit(VERITY_MONITOR_TYPES, handleSubscribeToggle, true);
    const { waiting, cols, sections, sort, filters, fltStats, paging, setSort, setFilter, setPaging } =
        useMonitorStatusTableState();
    const { items } = useMonitorStatusData();

    const [popup, setPopup] = useState<MonitorItem | null>(null);
    const tableRef = useRef<HTMLTableElement>(null);

    const handleSearch = useCallback(
        (query: DatafeedQuery): void => {
            lastQueryRef.current = query;
            setPopup(null);
            loadVerityMonitors(query);
        },
        [loadVerityMonitors],
    );

    // An in-app alert deep-link narrows it to that MonitorType with the In-App filter on.
    useEffect(() => {
        handleSearch({
            CE: ['ALL'],
            RX: ['ALL'],
            MonitorType: deepLinkMonitorType ? [deepLinkMonitorType] : ['ALL'],
            Status: ['PASS', 'FAIL', 'OFF'],
            SubscribedEmail: false,
            SubscribedInApp: !!deepLinkMonitorType,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const requestBulkSubscribe = useCallback(
        (subscribed: boolean, query: DatafeedQuery | null): void => {
            if (!query) return;
            // legacy guard: cannot subscribe to every facility AND every pharmacy at once
            if (
                subscribed &&
                query.CE.length === 1 &&
                query.CE[0] === 'ALL' &&
                query.RX.length === 1 &&
                query.RX[0] === 'ALL'
            ) {
                setInfoMsg('Cannot subscribe to All Facilities and All Pharmacies at once.');
                return;
            }
            lastQueryRef.current = query;
            setBulkPrompt({ subscribed, query, count: items.length });
        },
        [items.length],
    );

    const confirmBulkSubscribe = useCallback(async (): Promise<void> => {
        if (!bulkPrompt) return;
        const { subscribed, query } = bulkPrompt;
        setBulkPrompt(null);
        try {
            await datamonitorRepo.bulkSubscribe(query, subscribed);
            loadVerityMonitors(query);
        } catch {
            setSubscribeError(true);
        }
    }, [bulkPrompt, loadVerityMonitors]);

    const handleRowClick = useCallback((rowItem: MonitorItem): void => {
        setPopup(rowItem);
    }, []);

    const closePopup = useCallback((): void => {
        setPopup(null);
    }, []);

    const handleConfigSaved = useCallback((): void => {
        if (lastQueryRef.current) loadVerityMonitors(lastQueryRef.current);
    }, [loadVerityMonitors]);

    const subscribeErrorActions = useMemo(() => [{ label: 'OK', onClick: (): void => setSubscribeError(false) }], []);
    const infoActions = useMemo(() => [{ label: 'OK', onClick: (): void => setInfoMsg('') }], []);
    const bulkActions = useMemo(
        () => [
            { label: 'Cancel', onClick: (): void => setBulkPrompt(null), cssName: 'btn-secondary' },
            { label: 'OK', onClick: (): void => void confirmBulkSubscribe() },
        ],
        [confirmBulkSubscribe],
    );

    return (
        <div className="monitor-status-verity">
            <MonitorStatusSearchForm
                waiting={waiting}
                initialMonitorType={deepLinkMonitorType}
                onSearch={handleSearch}
            />

            {waiting ? (
                <TableSkeleton
                    columns={8}
                    rows={10}
                    sections={5}
                />
            ) : (
                <div className="monitor-status-table-wrapper">
                    <TableList>
                        <TableTop
                            id="monitorStatusVerityTable"
                            fltStats={fltStats}
                            topRight={
                                isVeritySA ? (
                                    <div className="monitor-bulk-actions">
                                        <Button
                                            id="monitorBulkSubBtn"
                                            cssName="btn-alt"
                                            onClick={() => requestBulkSubscribe(true, lastQueryRef.current)}
                                            disabled={waiting}
                                        >
                                            Bulk Subscribe
                                        </Button>
                                        <Button
                                            id="monitorBulkUnsubBtn"
                                            cssName="btn-delete"
                                            onClick={() => requestBulkSubscribe(false, lastQueryRef.current)}
                                            disabled={waiting}
                                        >
                                            Bulk Unsubscribe
                                        </Button>
                                    </div>
                                ) : undefined
                            }
                        />
                        <table ref={tableRef}>
                            <TableHead
                                cols={cols}
                                sections={sections}
                                sort={sort}
                                filters={filters}
                                setFilter={setFilter}
                                setSort={setSort}
                            />
                            <TableBody
                                cols={cols}
                                rows={items}
                                onClick={handleRowClick}
                            />
                        </table>
                        <TablePaging
                            paging={paging}
                            setPaging={setPaging}
                            fltStats={fltStats}
                        />
                    </TableList>
                </div>
            )}

            <MonitorConfigEditFlyOut
                monitorId={popup?.Id ?? null}
                monitorType={popup?.MonitorType ?? ''}
                title={popup ? `HISTORY for CE: ${popup.CE} - ${popup.RXVEN}` : ''}
                canEdit={isVeritySA}
                onClose={closePopup}
                onSaved={handleConfigSaved}
                ignoreClickRef={tableRef as React.RefObject<HTMLElement>}
            />

            <Dialog
                open={subscribeError}
                setOpen={setSubscribeError}
                title="Subscribe Error"
                message="Unable to update alert subscription."
                actions={subscribeErrorActions}
            />
            <Dialog
                open={!!infoMsg}
                setOpen={(): void => setInfoMsg('')}
                title="Cannot Subscribe"
                message={infoMsg}
                actions={infoActions}
            />
            <Dialog
                open={!!bulkPrompt}
                setOpen={(): void => setBulkPrompt(null)}
                title={bulkPrompt?.subscribed ? 'Bulk Subscribe' : 'Bulk Unsubscribe'}
                message={
                    bulkPrompt
                        ? `${bulkPrompt.count} monitor(s) matching your search will be ${
                              bulkPrompt.subscribed ? 'subscribed' : 'unsubscribed'
                          }.`
                        : ''
                }
                actions={bulkActions}
            />
        </div>
    );
};

export default MonitorStatusVerityTable;
